import { useCallback, useEffect, useRef, useState } from 'react';
import { getUserInfo, queryGroupInfo, getSubscriptionStatus, getEndpoint, subscribeToPlan, changePlan } from './net';
import { taskDetailAtom, userPermissionAtom, subscriptionsInfoAtom, balanceAtom, subscriptionsPreInfoAtom, logInfoAtom, showLoginAtom, animationStatesAtom, animationDependenciesAtom, isFeedbackModalOpenAtom, newUserPromotionTimeLeftAtom, upgradeModalAtom } from '../store';
import { useRecoilCallback, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import * as Sentry from "@sentry/react";
import { io } from 'socket.io-client';
import { useTips } from '../common/GlobalTips';
import { useTranslation } from 'react-i18next';
import { MD5 } from "./md5";
import { getGlobalTip } from "./tipsService";
import { createPortal } from 'react-dom'


// Debounce
export const useDebounce = (callback, delay) => {
    const timeoutRef = useRef();
    const lastCallRef = useRef(0);

    useEffect(() => () => {
        clearTimeout(timeoutRef.current);
    }, []);

    const debouncedCallback = (...args) => {
        const now = Date.now();
        const timeSinceLastCall = now - lastCallRef.current;

        if (timeSinceLastCall >= delay) {
            callback(...args);
            lastCallRef.current = now;
        } else {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                callback(...args);
                lastCallRef.current = Date.now();
            }, delay - timeSinceLastCall);
        }
    };

    return debouncedCallback;
};


// Throttle
export const useThrottle = (callback, delay = 300) => {
    const timeoutRef = useRef();
    const isWaitingRef = useRef(false);

    useEffect(() => () => {
        clearTimeout(timeoutRef.current);
    }, []);

    const throttledCallback = (...args) => {
        if (!isWaitingRef.current) {
            callback(...args);
            isWaitingRef.current = true;

            timeoutRef.current = setTimeout(() => {
                isWaitingRef.current = false;
            }, delay);
        }
    };

    return throttledCallback;
};

//RefreshInfo
export const useRefreshInfo = () => {
    const taskDetail = useRecoilValue(taskDetailAtom)
    const userPermission = useRecoilValue(userPermissionAtom)
    const setBalance = useSetRecoilState(balanceAtom);
    const setSubscriptionsInfo = useSetRecoilState(subscriptionsInfoAtom)
    const setSubscriptionsPreInfo = useSetRecoilState(subscriptionsPreInfoAtom)
    const subscriptionOverwrite = useRef(process.env.REACT_APP_DEFAULT_SUBSCRIPTION_PLAN)
    const [userData, setUserData] = useState(null);
    const [groupData, setGroupData] = useState(null);

    const refreshInfo = async (type) => {
        try {
            const eventMap = {
                'group': async () => {
                    const groupInfoData = (await queryGroupInfo(localStorage.getItem('group_uuid'))).data
                    if (groupInfoData.error) throw new Error(groupInfoData.error);
                    setBalance(pre => {
                        return {
                            ...pre,
                            groupBalance: groupInfoData.balance
                        }
                    })
                    setGroupData(groupInfoData)
                },
                'user': async () => {
                    const userData = (await getUserInfo({})).data;
                    if (userData.error) throw new Error(userData.error);
                    setBalance(pre => {
                        return {
                            ...pre,
                            selfBalance: userData.meta.balance
                        }
                    })

                    if (subscriptionOverwrite) {
                        const fakeSubscriptionData = {
                            plan_level: subscriptionOverwrite.current,
                        }
                        setSubscriptionsInfo(fakeSubscriptionData)
                    } else {
                        let subscriptionData = {}
                        subscriptionData = await getSubscriptionStatus()
                        if (subscriptionData?.data?.previous_subscriptions?.length) {
                            let PreItem = subscriptionData.data.previous_subscriptions
                            setSubscriptionsPreInfo(PreItem)
                        }
                        if (subscriptionData?.data?.active_subscriptions?.length) {
                            let subsArr = subscriptionData.data.active_subscriptions

                            setSubscriptionsPreInfo
                            const lastActiveItem = subsArr.reverse().find(item => item.active);
                            if (lastActiveItem) {
                                setSubscriptionsInfo(lastActiveItem)
                            }
                        }
                    }
                    setUserData(userData);
                },
                "all": async () => {
                    if (userPermission.chatavatar_tab_group) {
                        const groupInfoData = (await queryGroupInfo(localStorage.getItem('group_uuid'))).data
                        if (groupInfoData.error) throw new Error(groupInfoData.error);
                        setBalance(pre => {
                            return {
                                ...pre,
                                groupBalance: groupInfoData.balance
                            }
                        })
                        setGroupData(groupInfoData)
                    }
                    const userData = (await getUserInfo({})).data;
                    if (userData.error) throw new Error(userData.error);
                    setBalance(pre => {
                        return {
                            ...pre,
                            selfBalance: userData.meta.balance
                        }
                    })
                    setUserData(userData);
                }
            }
            if (eventMap[type]) {
                eventMap[type]();
            } else {
                if (taskDetail.group) {
                    eventMap['group']()
                } else {
                    eventMap['user']()
                }
            }

        } catch (e) {
            Sentry.captureException(e)
            console.error(e);
        }
    };
    return { refreshInfo, userData, groupData }
}
export const useLocalStorage = (name) => {
    const getLocalStorage = () => {
        let local = localStorage.getItem(name)
        if (typeof local == 'object' && local != null) {
            local = JSON.parse(local)
        }
        return local ? local : null
    }

    const setLocalStorage = (item) => {
        localStorage.setItem(name, JSON.stringify(item))
    }

    const removeLocalStorage = () => {
        return localStorage.removeItem(name)
    }

    return [getLocalStorage, setLocalStorage, removeLocalStorage]
}

export const useWebSocket = (initialPath) => {
    const socketRef = useRef(null);
    const [status, setSocketStatus] = useState('disconnected');
    const BASE_URL = getEndpoint('ws')
    const tip = useTips();
    const { t } = useTranslation();
    const deFaultpath = BASE_URL + initialPath;
    const maxReconnectAttempts = 5;
    const [reconnectAttempts, setReconnectAttempts] = useState(0);
    const keyInfo = useRef(null)
    const [error, setError] = useState(null);

    const connect = useCallback((subscription, uuid) => {
        if (reconnectAttempts < maxReconnectAttempts) {
            console.log('初始状态 connect reconnectAttempts', reconnectAttempts);
            initSocket(subscription, uuid);
            socketRef.current?.connect();
            setReconnectAttempts((attempts) => attempts + 1);
        }
    }, [reconnectAttempts]);

    const initSocket = useCallback((subscription, uuid) => {
        keyInfo.current = {
            subscription,
            uuid
        }
        if (socketRef.current) {
            console.log('disconnect');
            disconnect();
        }
        socketRef.current = io(deFaultpath, {
            query: {
                subscription,
            },
            transports: ['websocket']
        });

        socketRef.current.on('connect_error', (err) => {
            console.error('Connection error:', err);
            handleError(err, {
                errorType: 'connect_error',
                message: 'WebSocket connection error'
            });
        });

        socketRef.current.on('connect', () => {
            setSocketStatus('connected');
            if (uuid) {
                socketRef.current.emit('query', uuid);
            }
            console.log('Connected to WebSocket');
        });

        socketRef.current.on('disconnect', () => {
            // 触发重连逻辑
            console.log('Disconnected from WebSocket');
            setSocketStatus('disconnected');
        });

        socketRef.current.on('error', (error) => {
            console.error('WebSocket error:', error);
        });
    }, []);

    const handleError = useCallback((error, errorInfo = {}) => {
        // 设置本地错误状态
        setError(error);
        // 构建错误上下文
        const errorContext = {
            tags: {
                socketStatus: status,
                reconnectAttempts: reconnectAttempts,
                socketUrl: deFaultpath
            },
            extra: {
                ...errorInfo,
                subscription: keyInfo.current?.subscription,
                uuid: keyInfo.current?.uuid
            }
        };

        // 发送错误到 Sentry
        Sentry.withScope(scope => {
            // 添加标签和额外信息
            scope.setTags(errorContext.tags);
            scope.setExtras(errorContext.extra);

            // 捕获异常
            Sentry.captureException(error);
        });

        // 显示错误提示
        tip({
            type: 'warning',
            content: t('TIP_WRAN_WEBSOCKET_CONNECTION')
        });

        console.error('WebSocket error:', error, errorContext);
    }, [status, reconnectAttempts, deFaultpath]);

    const query = useCallback((uuid) => {
        socketRef.current.emit('query', uuid);
    }, []);

    const disconnect = useCallback(() => {
        if (socketRef.current) {
            socketRef.current.off();
            socketRef.current.disconnect();
        }
    }, []);

    const onMessage = useCallback((event, callback, onFailed) => {
        if (socketRef.current) {
            socketRef.current.on(event, (data, res) => {
                // console.log('onMessage ID', data);
                // console.log('onMessage jobStatus', res?.jobStatus);
                setReconnectAttempts(prevReconnectAttempts => {
                    if (res?.jobStatus === 'Failed') {  //Failed  Running
                        console.log('Failed - current reconnect attempts', prevReconnectAttempts);
                        if (prevReconnectAttempts >= maxReconnectAttempts) {
                            disconnect()
                            console.log('正在尝试重连', keyInfo.current);
                            setReconnectAttempts(0);
                            connect(keyInfo.current.subscription, keyInfo.current.uuid);
                            onFailed && onFailed();
                            return 0;
                        }
                        return prevReconnectAttempts + 1;
                    }
                    return prevReconnectAttempts;
                });
                return callback(data, res);
            });
        }
    }, []);

    return { status, connect, query, setSocketStatus, disconnect, onMessage, setReconnectAttempts };
};


export const useDebounceVal = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

export const useThrottleVal = (value, limit) => {
    const [throttledValue, setThrottledValue] = useState(value);
    const lastRan = useRef(Date.now());

    useEffect(() => {
        const handler = setTimeout(function () {
            if (Date.now() - lastRan.current >= limit) {
                setThrottledValue(value);
                lastRan.current = Date.now();
            }
        }, limit - (Date.now() - lastRan.current));

        return () => {
            clearTimeout(handler);
        };
    }, [value, limit]);

    return throttledValue;
}

export const useRequest = (callback, { deps = [], manual = false, initData = null } = {

}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(initData);

    const request = async (...args) => {
        setLoading(true);
        setError(null);
        try {
            const data = await callback(...args);
            setData(data);
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!manual) {
            request();
        }
    }, [...deps]);

    return { loading, error, request, data };
};

export const isPrerenderUserAgent = () => {
    return navigator.userAgent.includes('Hyper3D Prerender') || navigator.userAgent.includes('GoogleBot');
};

export const useDebounceEffect = (effect, deps, delay = 300) => {
    const latestEffect = useRef(effect);
    const latestDeps = useRef(deps);
    const timeoutRef = useRef(null);

    useEffect(() => {
        latestEffect.current = effect;
        latestDeps.current = deps;
    });

    useEffect(() => {
        const callEffect = () => {
            latestEffect.current();
        };

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(callEffect, delay);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [...deps, delay]);
};

export const useHoverBox = (options = {}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const ref = useRef(null);
    // 确保 `placement` 和 `offset` 是从 `options` 中解构出来的，而不是直接使用 `options` 对象
    // 这样可以避免 `options` 对象的引用变化导致不必要的重渲染。
    const {
        alignment = 'center',
        offset = { x: 0, y: 0 },
        placement = 'right'
    } = options;

    const updatePosition = useCallback(() => {
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            let top, left;
            console.log(placement, 'placement');

            switch (placement) {
                case 'top':
                    top = rect.top + window.scrollY;
                    left = rect.left + rect.width / 2 + window.scrollX;
                    break;
                case 'bottom':
                    top = rect.bottom + window.scrollY;
                    left = rect.left + rect.width / 2 + window.scrollX;
                    break;
                case 'left':
                    top = rect.top + rect.height / 2 + window.scrollY;
                    left = rect.left + window.scrollX;
                    break;
                case 'right':
                default:
                    top = rect.top + rect.height / 2 + window.scrollY;
                    left = rect.right + window.scrollX;
                    break;
            }

            setPosition({
                top: top + offset.y,
                left: left + offset.x
            });
        }
    }, [placement, offset]);

    useEffect(() => {
        if (isHovered) {
            updatePosition();
            window.addEventListener('scroll', updatePosition);
            window.addEventListener('resize', updatePosition);
        }
        return () => {
            window.removeEventListener('scroll', updatePosition);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isHovered, updatePosition]);

    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);

    return { ref, isHovered, position, handleMouseEnter, handleMouseLeave, alignment, placement };
};

export const useHeightTransition = (
    isExpanded,
    options = {}
) => {
    const {
        duration = 300,
        opentiming = 'ease',
        closetiming = 'ease',
        extraHeight = 0
    } = options;

    const elementRef = useRef(null);

    useEffect(() => {
        const element = elementRef.current;
        if (!element) return;

        if (isExpanded) {
            // 展开过程
            element.style.height = 'auto';
            const height = element.getBoundingClientRect().height;
            element.style.height = '0px';
            element.style.transition = `height ${duration}ms ${opentiming}`;
            element.offsetHeight; // 强制重绘
            element.style.height = `${Math.floor(height + extraHeight)}px`;
        } else {
            // 收起过程
            element.style.height = `${element.getBoundingClientRect().height}px`;
            element.offsetHeight; // 强制重绘
            element.style.transition = `height ${duration}ms ${closetiming}`;
            element.style.height = '0px';
        }

        // 过渡结束后清理
        const handleTransitionEnd = () => {
            if (isExpanded) {
                element.style.height = 'auto';
            }
        };

        element.addEventListener('transitionend', handleTransitionEnd);

        return () => {
            element.removeEventListener('transitionend', handleTransitionEnd);
        };
    }, [isExpanded, duration, opentiming, closetiming, extraHeight]);

    return elementRef;
};

export const useCheckLogin = () => {
    const logInfo = useRecoilValue(logInfoAtom);
    const setShowLogin = useSetRecoilState(showLoginAtom);

    const checkLogin = () => {
        if (!logInfo) {
            setShowLogin(true);
            return false;
        }
        return true;
    };

    return { checkLogin };
};


/**
 * 创建点击涟漪效果的 Hook
 * @param {string} rippleClassName - 涟漪效果的 CSS 类名
 * @param {Object} options - 配置选项
 * @param {string} options.color - 涟漪颜色，默认为 'rgba(255, 255, 255, 0.7)'
 * @param {number} options.duration - 动画持续时间(ms)，默认为 600
 * @param {number} options.scale - 涟漪最大缩放比例，默认为 4
 * 
 * @example
 * // 基础使用方法
 * function MyButton() {
 *   const buttonRef = useRef(null);
 *   const createRipple = useRippleEffect('ripple-effect');
 * 
 *   return (
 *     <button 
 *       ref={buttonRef}
 *       onClick={(e) => createRipple(e, buttonRef.current)}
 *       style={{ position: 'relative', overflow: 'hidden' }}
 *     >
 *       Click Me
 *     </button>
 *   );
 * }
 * 
 * // 自定义配置使用方法
 * function CustomButton() {
 *   const buttonRef = useRef(null);
 *   const createRipple = useRippleEffect('ripple-effect', {
 *     color: 'rgba(0, 0, 0, 0.3)',
 *     duration: 800,
 *     scale: 6
 *   });
 * 
 *   return (
 *     <button 
 *       ref={buttonRef}
 *       onClick={(e) => createRipple(e, buttonRef.current)}
 *       style={{ position: 'relative', overflow: 'hidden' }}
 *     >
 *       Custom Ripple
 *     </button>
 *   );
 * }
 * 
 * // 需要在 CSS 中定义的样式
 * // .ripple-effect {
 * //   position: absolute;
 * //   border-radius: 50%;
 * //   transform: scale(0);
 * //   animation: ripple 600ms linear;
 * //   pointer-events: none;
 * // }
 * // @keyframes ripple {
 * //   to {
 * //     transform: scale(4);
 * //     opacity: 0;
 * //   }
 * // }
 */
export const useRippleEffect = (rippleClassName, options = {}) => {
    const {
        color = 'rgba(255, 255, 255, 0.7)',
        duration = 600,
        scale = 4
    } = options;

    const createRipple = useCallback((event, element) => {
        const ripple = document.createElement('span');
        const rect = element.getBoundingClientRect();

        // 计算点击位置相对于元素的位置（包含 padding）
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        // 计算涟漪直径（取最大值确保覆盖整个元素）
        const diameter = Math.max(
            Math.max(
                Math.abs(y),
                Math.abs(rect.height - y)
            ) * 2,
            Math.max(
                Math.abs(x),
                Math.abs(rect.width - x)
            ) * 2
        );

        const radius = diameter / 2;

        // 设置涟漪样式
        ripple.style.width = ripple.style.height = `${diameter}px`;
        ripple.style.left = `${x - radius}px`;
        ripple.style.top = `${y - radius}px`;
        ripple.style.backgroundColor = color;
        ripple.style.animationDuration = `${duration}ms`;
        ripple.className = rippleClassName;

        // 创建动态样式
        const styleElement = document.createElement('style');
        styleElement.textContent = `
            @keyframes ripple {
                to {
                    transform: scale(${scale});
                    opacity: 0;
                }
            }
        `;
        document.head.appendChild(styleElement);

        // 清理之前的涟漪效果
        const existingRipples = element.getElementsByClassName(rippleClassName);
        Array.from(existingRipples).forEach(oldRipple => oldRipple.remove());

        element.appendChild(ripple);

        ripple.addEventListener('animationend', () => {
            ripple.remove();
            styleElement.remove();
        });
    }, [rippleClassName, color, duration, scale]);

    return createRipple;
};

export const useAnimationController = () => {
    const [animationStates, setAnimationStates] = useRecoilState(animationStatesAtom)
    const [dependencies, setDependencies] = useRecoilState(animationDependenciesAtom)

    // Register animation and its dependencies
    const register = (id, config) => {
        // Initialize animation state
        setAnimationStates(prev => ({
            ...prev,
            [id]: {
                isActive: false,
                completed: false,
                conditions: config.conditions || {}
            }
        }))

        // Set dependency relationship
        setDependencies(prev => ({
            ...prev,
            [id]: {
                next: config.next || [],
                dependencies: config.dependencies || []
            }
        }))
    }

    // Start executing animation
    const start = useRecoilCallback(({ snapshot }) => async (id) => {
        const deps = await snapshot.getPromise(animationDependenciesAtom)
        const states = await snapshot.getPromise(animationStatesAtom)

        // Check if all dependencies are satisfied
        const canStart = deps[id]?.dependencies?.every(depId =>
            states[depId]?.completed
        ) ?? true

        if (canStart) {
            setAnimationStates(prev => ({
                ...prev,
                [id]: { ...prev[id], isActive: true }
            }))
            return true
        }
        return false
    })

    // Complete animation
    const complete = useRecoilCallback(({ snapshot }) => async (id) => {
        setAnimationStates(prev => ({
            ...prev,
            [id]: { ...prev[id], isActive: false, completed: true }
        }))

        const deps = await snapshot.getPromise(animationDependenciesAtom)
        const currentDep = deps[id]

        // Trigger all subsequent animations
        if (currentDep?.next?.length) {
            currentDep.next.forEach(({ id: nextId, trigger, delay = 0 }) => {
                if (trigger === 'auto') {
                    setTimeout(() => {
                        start(nextId)
                    }, delay)
                }
            })
        }
    })

    return {
        register,
        start,
        complete,
        animationStates
    }
};

export function SetThrottling(axiosInstance, time) {
    const throttlingTokenCancel = new Map()
    axiosInstance.interceptors.request.use(config => {
        if (config._throttlingBypass) {
            return config;
        }

        const nowTime = new Date().getTime()
        const tokenKey = MD5(JSON.stringify(config))
        if (nowTime - (throttlingTokenCancel.get(tokenKey) ?? 0) < time) {
            const tip = getGlobalTip();
            tip({
                type: 'primary',
                content: 'You are tapping too fast. Take a break',
            });
            return Promise.reject(new Error('THROTTLING_TRIGGERED'))
        } else {
            throttlingTokenCancel.set(tokenKey, nowTime)
        }
        return config
    })
    return axiosInstance
}


export const usePromotionsFetch = () => {
    const tip = useTips(null)
    const { t } = useTranslation();
    const setIsFeedbackModalOpen = useSetRecoilState(isFeedbackModalOpenAtom);
    const subscriptionsInfo = useRecoilValue(subscriptionsInfoAtom);
    const logInfo = useRecoilValue(logInfoAtom);
    const setShowLogin = useSetRecoilState(showLoginAtom);
    const [loading, setLoading] = useState(false);
    const [upgradeModal, setUpgradeModal] = useRecoilState(upgradeModalAtom);

    const handleClose = () => {
        setUpgradeModal({
            visible: false,
            info: null
        });
    };

    const handleConfirmPlan = async () => {
        setLoading(true);
        try {
            const result = await changePlan(upgradeModal.info);
            if (result?.data?.error) {
                setLoading(false);
                tip({
                    type: 'error',
                    presetText: 'ERROR',
                    content: result?.data?.error,
                });
            } else {
                handleClose();
                setLoading(false);
                tip({
                    type: 'success',
                    content: t('TIP_SUCCESS_SUB_PLAN_CHANGED'),
                });
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            }
        } catch (e) {
            if (e.message === 'OPERATION_NOT_SUPPORTED') {
                tip({
                    type: 'error',
                    content: t('TIP_ERR_NOT_SUPPORTED_SUB'),
                });
                setIsFeedbackModalOpen(true);
            }
        }
    };

    const showUpgradeModal = (info) => {
        setUpgradeModal({
            visible: true,
            info
        });
    };

    const handleSubscribePromotion = async (index) => {
        if (!logInfo) {
            setShowLogin(true);
            return;
        }

        const pricingTiers = [
            {
                type: "Creator",
                price: 24,
                credits: 360,
                discount: 50,
                plan_monthly_price: 30,
                yearlyCredits: 30 * 12
            },
            {
                type: "Business",
                price: 96,
                credits: 2496,
                discount: 50,
                plan_monthly_price: 120,
                yearlyCredits: 120 / 1.5 * 1.3 * 2 * 12
            },
        ]
        const param = {
            billing_provider: 'Stripe',
            plan_level: pricingTiers[index].type,
            plan_monthly_price: pricingTiers[index].plan_monthly_price,
            yearly: true,
            promotion_code: "xmas_50_off_2024"
        }

        const compareCredits = (currentStatus, targetParam) => {

            if (subscriptionsInfo.plan_level === 'Unlimited') {
                if (targetParam.plan_level === 'Unlimited') {
                    const targetMonthNum = currentStatus?.month_num // 目标月数
                    // 计算当前订阅的月数
                    const startDate = new Date(subscriptionsInfo?.start_time);
                    const endDate = new Date(subscriptionsInfo?.end_time);
                    const diffTime = Math.abs(endDate - startDate);
                    const diffMonths = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 30.44));
                    if (diffMonths < targetMonthNum) {
                        showUpgradeModal(targetParam)
                    } else if (diffMonths === targetMonthNum) {
                        tip({
                            type: 'primary',
                            content: t('TIP_PRIMARY_SUB_CURRENT_PLAN'),
                        });
                    } else {
                        tip({
                            type: 'primary',
                            content: t('TIP_PRIMARY_SUBSCRIBE_PLAN'),
                        });
                        setIsFeedbackModalOpen(true)
                    }
                    return
                } else {
                    tip({
                        type: 'primary',
                        content: t('TIP_PRIMARY_SUBSCRIBE_PLAN'),
                    });
                    setIsFeedbackModalOpen(true)
                    return
                }
            }
            let currentCreditsYearly = Number(subscriptionsInfo.plan_level === 'Creator' ? subscriptionsInfo.plan_price : (subscriptionsInfo.plan_price / 1.5 * 1.3 * 2)) * (subscriptionsInfo.plan_yearly ? 12 : 1)
            let targetCreditsYearly = Number(currentStatus.yearlyCredits)
            if (targetParam.plan_level === 'Unlimited') {
                targetCreditsYearly = Infinity
            }

            if (targetCreditsYearly > currentCreditsYearly) {
                showUpgradeModal(targetParam)
            } else if (subscriptionsInfo.plan_level === currentStatus.type && targetCreditsYearly === currentCreditsYearly) {
                // 同级
                tip({
                    type: 'primary',
                    content: t('TIP_PRIMARY_SUB_CURRENT_PLAN'),
                });
            } else {
                // 降级
                tip({
                    type: 'primary',
                    content: t('TIP_PRIMARY_SUBSCRIBE_PLAN'),
                });
                setIsFeedbackModalOpen(true)
            }
        }

        if (subscriptionsInfo?.plan_level) {
            compareCredits(pricingTiers[index], param);
        } else {
            const result = await subscribeToPlan(param);
            if (result?.data?.url) {
                window.location.href = result?.data?.url;
            }
        }
    };

    return {
        handleSubscribePromotion,
        upgradeModal,
        handleClose,
        handleConfirmPlan,
        loading
    };
};


const STORAGE_KEY = 'newUserPromotionTimeLeft';

export const useNewUserPromotionCountdown = () => {
    const [timeLeft, setTimeLeft] = useRecoilState(newUserPromotionTimeLeftAtom);
    const isRunningRef = useRef(false);
    const startTimeRef = useRef(null);
    const currentUUID = localStorage.getItem('user_uuid');

    // 保存初始时间戳到本地存储
    const saveInitialTimestamp = () => {
        const now = Date.now();
        const stored = localStorage.getItem(STORAGE_KEY);
        const data = stored ? JSON.parse(stored) : {};

        data[currentUUID] = {
            startTimestamp: now,
            totalDuration: 24 * 60 * 60 * 1000
        };

        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        startTimeRef.current = now;
    };

    // 从本地存储恢复并计算剩余时间
    const restoreFromStorage = () => {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const data = JSON.parse(stored);
            const userTimer = data[currentUUID];

            if (userTimer) {
                const { startTimestamp, totalDuration } = userTimer;
                const now = Date.now();
                const elapsed = now - startTimestamp;
                const remaining = totalDuration - elapsed;

                if (remaining > 0) {
                    const remainingSeconds = Math.floor(remaining / 1000);
                    const newTime = {
                        hours: Math.floor(remainingSeconds / 3600),
                        minutes: Math.floor((remainingSeconds % 3600) / 60),
                        seconds: remainingSeconds % 60
                    };
                    setTimeLeft(prev => ({
                        ...prev,
                        [currentUUID]: newTime
                    }));
                    startTimeRef.current = startTimestamp;
                    return true;
                }
            }
        }
        return false;
    };

    // 启动倒计时
    const startCountdown = useCallback(() => {
        if (isRunningRef.current || !currentUUID) return;

        if (!startTimeRef.current) {
            saveInitialTimestamp();
        }

        isRunningRef.current = true;

        const timer = setInterval(() => {
            const now = Date.now();
            const elapsed = now - startTimeRef.current;
            const remaining = (24 * 60 * 60 * 1000) - elapsed;

            if (remaining <= 0) {
                clearInterval(timer);
                isRunningRef.current = false;
                // 只移除当前用户的计时器数据
                const stored = localStorage.getItem(STORAGE_KEY);
                if (stored) {
                    const data = JSON.parse(stored);
                    delete data[currentUUID];
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
                }
                setTimeLeft(prev => ({
                    ...prev,
                    [currentUUID]: { hours: 0, minutes: 0, seconds: 0 }
                }));
                return;
            }

            const remainingSeconds = Math.floor(remaining / 1000);
            setTimeLeft(prev => ({
                ...prev,
                [currentUUID]: {
                    hours: Math.floor(remainingSeconds / 3600),
                    minutes: Math.floor((remainingSeconds % 3600) / 60),
                    seconds: remainingSeconds % 60
                }
            }));
        }, 1000);

        return () => clearInterval(timer);
    }, [setTimeLeft, currentUUID]);

    // 暂停倒计时
    const pauseCountdown = useCallback(() => {
        isRunningRef.current = false;
    }, []);

    // 重置倒计时
    const resetCountdown = useCallback(() => {
        pauseCountdown();
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const data = JSON.parse(stored);
            delete data[currentUUID];
            localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        }
        startTimeRef.current = null;
        setTimeLeft(prev => ({
            ...prev,
            [currentUUID]: { hours: 24, minutes: 0, seconds: 0 }
        }));
    }, [pauseCountdown, setTimeLeft, currentUUID]);

    // 组件挂载时恢复时间
    useEffect(() => {
        if (!currentUUID) return;

        const hasRestoredTime = restoreFromStorage();
        if (!hasRestoredTime) {
            setTimeLeft(prev => ({
                ...prev,
                [currentUUID]: { hours: 24, minutes: 0, seconds: 0 }
            }));
        }

        return () => {
            if (!document.querySelector('[data-countdown-user]')) {
                pauseCountdown();
            }
        };
    }, [currentUUID]);

    return {
        timeLeft: timeLeft[currentUUID] || { hours: 24, minutes: 0, seconds: 0 },
        startCountdown,
        pauseCountdown,
        resetCountdown,
        isRunning: isRunningRef.current
    };
};


